import { useQuery } from '@tanstack/react-query'
import { getComponents } from '../lib/search-service'
import { useOffline } from '@/components/offline-context'

export function useComponents(
  word: string, // The full word (e.g. "制服")
  isTraditionalMode: boolean
) {
  const { isOfflineEnabled, isReady, isQueryEnabled } = useOffline()
  const isOffline = isOfflineEnabled && isReady

  // 1. Split word into unique characters
  // "xixi" -> ["x", "i", "x", "i"] -> Set -> ["x", "i"]
  const chars = Array.from(new Set(word.split('')))

  // 2. Only fetch if it's actually a compound word (>1 char)
  const shouldFetch = chars.length > 1

  return useQuery({
    // Remove isOffline from query key to prevent cache invalidation
    // The queryFn will handle offline/online mode internally
    queryKey: ['components', word, isTraditionalMode],
    queryFn: async () => {
      if (!shouldFetch) return []
      return await getComponents(chars, isOffline, isTraditionalMode)
    },
    enabled: isQueryEnabled(shouldFetch),
    // Components data rarely changes, cache aggressively
    staleTime: 1000 * 60 * 60 * 24, // 24 hours
    // Keep previous data visible during transitions and tab switches
    placeholderData: (previousData) => previousData,
    gcTime: 1000 * 60 * 60 * 24 * 7, // Keep in cache for 7 days
    refetchOnMount: false, // Don't refetch when component mounts if data is fresh
    refetchOnWindowFocus: false, // Don't refetch on window focus
    refetchOnReconnect: false // Don't refetch on reconnect
  })
}
